import { useI18n } from '@/context/I18nContext';
import { CrudPage, type CrudColumn, type CrudField } from '@/components/crud/CrudPage';

interface Chassis {
  id: string;
  brand: string;
  type: string;
  productionYear?: string;
  nr?: string;
  teamId: string;
}

export function ChassisTab({ teamId, readOnly }: { teamId: string; readOnly?: boolean }) {
  const { t } = useI18n();

  const columns: CrudColumn<Chassis>[] = [
    { key: 'brand', label: t('common.brand') },
    {
      key: 'type',
      label: t('common.type'),
      render: (c) => (
        <span className="inline-block px-2.5 py-0.5 bg-accent-subtle border border-accent/20 rounded-full text-xs font-medium text-accent-hover tracking-wide">
          {c.type}
        </span>
      ),
    },
    {
      key: 'productionYear',
      label: t('common.year'),
      className: 'w-[80px] text-text-secondary tabular-nums text-sm',
      render: (c) => c.productionYear || <span className="text-text-muted">—</span>,
    },
    {
      key: 'nr',
      label: t('common.nr'),
      className: 'w-[80px] text-text-secondary tabular-nums text-sm',
      render: (c) => c.nr || <span className="text-text-muted">—</span>,
    },
  ];

  const fields: CrudField[] = [
    { key: 'brand', label: t('common.brand'), placeholder: 'e.g. Tony Kart' },
    { key: 'type', label: t('common.type'), placeholder: 'e.g. Racer 401RR' },
    { key: 'productionYear', label: t('common.year'), placeholder: 'e.g. 2024', optional: true },
    { key: 'nr', label: t('common.nr'), placeholder: 'e.g. 1', info: t('common.nrInfo'), optional: true, defaultValue: (items) => String(items.length + 1) },
  ];

  return (
    <CrudPage<Chassis>
      title={t('materials.tab.chassis')}
      entityName={t('materials.entity.chassis')}
      emptyIcon="🏎️"
      columns={columns}
      fields={fields}
      listMethod="ListChassis"
      createMethod="CreateChassis"
      updateMethod="UpdateChassis"
      deleteMethod="DeleteChassis"
      formGridClass="grid-cols-1 sm:grid-cols-2 md:grid-cols-[1fr_1fr_auto_auto]"
      listParams={{ teamId }}
      readOnly={readOnly}
      hiddenValues={{ teamId }}
    />
  );
}
